/* eslint-disable react-refresh/only-export-components */
import ScreenRouteNames from "@/constants/screen-route-names";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { LogTags } from "./logger-types";
import {
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
} from "./logger-types";

export type LoggerContextState = {
  screenName?: string; // From ScreenRouteNames (e.g., "MY_BENEFIT")
  tag?: LogTags;
  category?: LogCategories;
  subCategory?: LogSubCategories;
  type?: LogEventTypeEnum;
};

export type DialogContext = {
  dialogName: string;
  openedFrom?: string;
  dialogAction?: string;
  openedAtMs?: number;
} & LoggerContextState;

export type PanelContext = {
  panelName: string;
  panelAction?: string;
  openedFrom?: string;
} & LoggerContextState;

/** Key-value form data for logging (e.g. on submit). Do not store sensitive fields like password. */
export type FormLogContext = Record<string, unknown>;

/** Form control names to exclude from logged context (case-insensitive). */
export const SENSITIVE_FORM_KEYS = [
  "password",
  "confirmpassword",
  "confirm_password",
  "currentpassword",
  "current_password",
  "newpassword",
  "new_password",
  "ssn",
] as const;

/**
 * Get the key to use for a form control in log context.
 * Prefers id (present on most components) and falls back to name.
 */
function getFormControlKey(
  el:
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement
    | HTMLButtonElement,
): string | null {
  const id = (el as HTMLInputElement).id?.trim();
  const name = (el as HTMLInputElement).name?.trim();
  return id || name || null;
}

/**
 * Get the non-sensitive value for a form control for logging.
 */
function getFormControlValue(
  el:
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement
    | HTMLButtonElement,
): string | undefined {
  // Support Shadcn/Radix primitives like Checkbox/Switch that render as buttons
  if (el instanceof HTMLButtonElement) {
    const role = el.getAttribute("role");
    if (role === "checkbox" || role === "switch" || role === "radio") {
      const ariaChecked = el.getAttribute("aria-checked");
      const dataState = el.dataset?.state;
      const isChecked = ariaChecked === "true" || dataState === "checked";
      return isChecked ? "true" : undefined;
    }
    return undefined;
  }

  const input = el as HTMLInputElement;
  if (input.type === "checkbox" || input.type === "radio") {
    return input.checked ? input.value || "true" : undefined;
  }
  if (input.type === "file" && input.files?.length) {
    return "[File]";
  }
  const v = (el as HTMLInputElement).value;
  return v === "" ? undefined : v;
}

/**
 * Find the tablist that is the closest ancestor context for a form (form inside TabsContent).
 * Returns the selected tab's value for that tablist, or undefined if none.
 */
function getActiveTabValueForForm(
  form: HTMLFormElement,
): Record<string, string> {
  const tabContext: Record<string, string> = {};
  let el: Element | null = form.parentElement;
  while (el) {
    // const tablist = el.querySelector("[role='tablist']");
    // if (tablist && el.contains(form)) {
    //   const selected =
    //     tablist.querySelector('[role="tab"][data-state="active"]') ||
    //     tablist.querySelector('[role="tab"][aria-selected="true"]');
    //   if (selected) {
    //     const tabValue =
    //       (selected.textContent?.trim() ?? "") ||
    //       selected.getAttribute("data-value") ||
    //       (selected as HTMLButtonElement).value ||
    //       selected.getAttribute("value") ||
    //       selected.id;
    //     const key = tablist.id?.trim() || "activeTab";
    //     tabContext[key] = tabValue;
    //   }
    //   break;
    // }
    el = el.parentElement;
  }
  return tabContext;
}

/**
 * Build key-value form context from a form element (for submit-button logging).
 * Uses id when present (as in most components), otherwise name.
 * For keys in SENSITIVE_FORM_KEYS, keeps the key but masks the value as "****".
 * Includes Shadcn/Radix Tabs: adds active tab value (key: tablist id or "activeTab").
 */
export function getFormContextFromFormElement(
  form: HTMLFormElement,
): FormLogContext {
  const context: FormLogContext = {};
  const sensitive = new Set(SENSITIVE_FORM_KEYS.map((k) => k.toLowerCase()));

  for (let i = 0; i < form.elements.length; i++) {
    const el = form.elements[i];
    const isInputLike =
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement;
    const isRadixCheckboxButton =
      el instanceof HTMLButtonElement &&
      ["checkbox", "switch", "radio"].includes(
        (el.getAttribute("role") || "").toLowerCase(),
      );
    const isTabTrigger =
      el instanceof HTMLButtonElement &&
      (el.getAttribute("role") || "").toLowerCase() === "tab";
    if (isTabTrigger) continue;

    if (!isInputLike && !isRadixCheckboxButton) continue;

    const key = getFormControlKey(el);
    if (!key) continue;

    const value = getFormControlValue(el);
    const keyLower = key.toLowerCase();

    if (sensitive.has(keyLower)) {
      // Preserve the key but mask sensitive value.
      context[key] = "***";
    } else if (value !== undefined) {
      context[key] = value;
    }
  }

  Object.assign(context, getActiveTabValueForForm(form));
  return context;
}

class LoggerContextManager {
  private globalContext: LoggerContextState = {};
  private dialogStack: DialogContext[] = [];
  private listeners: Set<() => void> = new Set();
  private formContext: FormLogContext = {};
  private panelStack: PanelContext[] = [];

  setGlobalContext(context: Partial<LoggerContextState>) {
    this.globalContext = { ...this.globalContext, ...context };
    this.notifyListeners();
  }

  setFormContext(context: FormLogContext) {
    this.formContext = { ...context };
  }

  getFormContext(): FormLogContext {
    return { ...this.formContext };
  }

  clearFormContext() {
    this.formContext = {};
  }

  getGlobalContext(): LoggerContextState {
    return { ...this.globalContext };
  }

  pushDialogContext(dialog: DialogContext) {
    this.dialogStack.push(dialog);
    this.notifyListeners();
  }

  popDialogContext(): DialogContext | undefined {
    const popped = this.dialogStack.pop();
    this.notifyListeners();
    return popped;
  }

  getCurrentDialogContext(): DialogContext | undefined {
    return this.dialogStack[this.dialogStack.length - 1];
  }

  /**
   * Panel context stack (similar to dialogs) so nested panels don't clobber each other.
   * Use set/clear as push/pop semantics.
   */
  setPanelContext(context: PanelContext) {
    this.panelStack.push(context);
    this.notifyListeners();
  }

  popPanelContext(): PanelContext | undefined {
    const popped = this.panelStack.pop();
    this.notifyListeners();
    return popped;
  }

  getCurrentPanelContext(): PanelContext | undefined {
    return this.panelStack[this.panelStack.length - 1];
  }

  getPanelContext(): PanelContext | undefined {
    const current = this.getCurrentPanelContext();
    return current ? { ...current } : undefined;
  }

  clearPanelContext() {
    if (this.panelStack.length === 0) return;
    this.panelStack.pop();
    this.notifyListeners();
  }

  getActiveContext(): LoggerContextState {
    const dialogContext = this.getCurrentDialogContext();
    const panelContext = this.getCurrentPanelContext();

    // Precedence:
    // global (route) < panel < dialog (dialog is the top-most overlay)
    if (dialogContext) {
      return {
        ...this.globalContext,
        ...(panelContext ?? {}),
        ...dialogContext,
      };
    }
    if (panelContext) {
      return { ...this.globalContext, ...panelContext };
    }
    return this.globalContext;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  clear() {
    this.globalContext = {};
    this.dialogStack = [];
    this.formContext = {};
    this.panelStack = [];
    this.notifyListeners();
  }
}

export const loggerContextManager = new LoggerContextManager();

export function useLoggerContext() {
  const [context, setContext] = useState(() =>
    loggerContextManager.getActiveContext(),
  );

  useEffect(() => {
    return loggerContextManager.subscribe(() => {
      setContext(loggerContextManager.getActiveContext());
    });
  }, []);

  return context;
}

/**
 * Map route path to screen metadata from ScreenRouteNames.
 * Prefers exact matches; falls back to includes for nested paths.
 */
export function getScreenMetadataFromPath(path: string): {
  screenName: string;
  tag: LogTags;
  category: LogCategories;
  subCategory: LogSubCategories;
} | null {
  const cleanPath = path.split("?")[0].split("#")[0].replace(/\/$/, "") || "/";

  const entries = Object.entries(ScreenRouteNames);

  // Try exact match first (/${path})
  for (const [key, screen] of entries) {
    const screenName = screen.path === "/" ? "GROUP" : key;
    const screenPath =
      screen.path === "/" || screen.path === ""
        ? "/"
        : `/${screen.path}`.replace(/\/$/, "");
    if (cleanPath === screenPath) {
      return {
        screenName,
        tag: screen.tag,
        category: screen.category || LogCategories.NONE,
        subCategory: screen.subCategory || LogSubCategories.NONE,
      };
    }
  }

  // Fallback: match by includes (for nested paths like /cost-estimator/results)
  const sortedByPathLength = [...entries].sort(
    (a, b) => (b[1].path?.length ?? 0) - (a[1].path?.length ?? 0),
  );
  for (const [key, screen] of sortedByPathLength) {
    const screenName = screen.path === "/" ? "GROUP" : key;

    if (!screen.path) continue;
    const segment = `/${screen.path}`;
    if (cleanPath === "/" || cleanPath.includes(segment)) {
      return {
        screenName,
        tag: screen.tag,
        category: screen.category || LogCategories.NONE,
        subCategory: screen.subCategory || LogSubCategories.NONE,
      };
    }
  }

  return null;
}

/**
 * Get intelligently inferred component name from button
 * Priority: logAction prop > button text > "BUTTON"
 */
export function inferActionName(
  logAction?: string,
  buttonText?: string,
): string {
  if (logAction) return logAction;

  if (buttonText) {
    return buttonText.replace(/\s+/g, "_").replace(/[^\w]/g, "");
  }

  return LogEventTypeEnum.USER_INTERACTION;
}

/**
 * Build action title for button clicks, using dialog context when available.
 * Uses getCurrentDialogContext() at call time so "x button clicked from y dialog" works
 * when the button is inside an open dialog.
 * @param buttonText - Button label
 * @param actionName - Fallback when buttonText is empty
 * @param dialogNameOverride - Use when context may be popped before handler (e.g. close buttons)
 * @returns Title like "CLOSE BUTTON CLICKED FROM CHANGE PASSWORD DIALOG" or "CLOSE BUTTON CLICKED"
 */
export function buildButtonActionTitle(
  buttonText: string,
  actionName: string,
  dialogNameOverride?: string,
): string {
  const dialogContext = loggerContextManager.getCurrentDialogContext();
  const _action = (buttonText || actionName).trim();
  const _context = dialogNameOverride ?? dialogContext?.dialogName;

  if (_context && _action) {
    return `${String(_action).replace(/\s+/g, " ")} BUTTON CLICKED FROM ${String(_context).replace(/\s+/g, " ")} DIALOG`;
  }
  if (_action) {
    return `${LogEventTypeEnum.USER_INTERACTION}: ${String(_action).replace(/\s+/g, " ")}`;
  }
  return actionName;
}

type PanelTrackerProps = {
  panelName: string;
  /** Optional overrides so panel context can supersede route context while open. */
  category?: LogCategories;
  subCategory?: LogSubCategories;
  tag?: LogTags;
  children: ReactNode;
};

export function PanelTracker({
  panelName,
  category,
  subCategory,
  tag,
  children,
}: PanelTrackerProps) {
  useEffect(() => {
    loggerContextManager.setPanelContext({
      panelName,
      ...(category ? { category } : {}),
      ...(subCategory ? { subCategory } : {}),
      ...(tag ? { tag } : {}),
    });
    return () => {
      loggerContextManager.clearPanelContext();
    };
  }, [panelName, category, subCategory, tag]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
